import { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { keyframes } from "@emotion/react";
import { Icon } from "@iconify/react";

/**
 * Promo baneri kao karusel: u fokusu je uvek jedna velika slika, susedne vire sa
 * strane umanjene i zatamnjene. Vrti se u krug i sam, a zaustavlja se čim korisnik
 * pređe mišem ili prevuče prstom — da mu se sadržaj ne izmakne dok gleda.
 *
 * Bez karusel biblioteke: pomeranje je čist transform po indeksu, pa je i
 * beskonačno vrtenje samo modularna aritmetika, bez kloniranja pločica.
 */

const CAPTION_RATIO = { xs: "4 / 3", sm: "16 / 9", md: "1717 / 916" };
const IMAGE_RATIO = "1717 / 916";
const CARD_WIDTH = { xs: "86%", sm: "78%", md: "70%" };
const AUTOPLAY_MS = 5000;
const SWIPE_THRESHOLD = 50;

const SIDE_SHIFT = 62;
const SIDE_SCALE = 0.84;

const fill = keyframes`
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
`;

export default function PromoBannerSlider({ banners }) {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const count = banners.length;
    const ratio = banners.some((banner) => banner.content) ? CAPTION_RATIO : IMAGE_RATIO;

    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const dragStart = useRef(null);

    const go = useCallback((delta) => setActive((i) => (i + delta + count) % count), [count]);

    useEffect(() => {
        setActive(0);
    }, [count]);

    const autoplay = !paused && !reduceMotion && count > 1;

    useEffect(() => {
        if (!autoplay) return undefined;
        const id = setTimeout(() => go(1), AUTOPLAY_MS);
        return () => clearTimeout(id);
    }, [autoplay, active, go]);

    // Najkraći put do pločice: sa 3 banera daje -1, 0, 1 i kad se pređe kraj niza.
    const offsetOf = (index) => {
        const raw = index - active;
        const half = Math.floor(count / 2);
        if (raw > half) return raw - count;
        if (raw < -half) return raw + count;
        return raw;
    };

    const onPointerDown = (e) => {
        dragStart.current = e.clientX ?? e.touches?.[0]?.clientX ?? null;
        setPaused(true);
    };

    const onPointerUp = (e) => {
        const start = dragStart.current;
        dragStart.current = null;
        if (start == null) return;
        const end = e.clientX ?? e.changedTouches?.[0]?.clientX ?? start;
        const delta = end - start;
        if (Math.abs(delta) > SWIPE_THRESHOLD) go(delta < 0 ? 1 : -1);
        setPaused(false);
    };

    return (
        <Box
            sx={{ position: "relative" }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={onPointerDown}
            onTouchEnd={onPointerUp}
            onMouseDown={onPointerDown}
            onMouseUp={onPointerUp}
        >
            <Box sx={{ position: "relative", overflow: "hidden", py: { xs: 1, md: 2 } }}>
                {/* Nevidljivi držač visine: karusel je apsolutno pozicioniran, pa mu
                    okvir mora doći od pločice u fokusu. */}
                <Box sx={{ width: CARD_WIDTH, aspectRatio: ratio, mx: "auto", visibility: "hidden" }} />

                {banners.map((banner, index) => {
                    const offset = offsetOf(index);
                    const isActive = offset === 0;
                    const isNear = Math.abs(offset) <= 1;
                    return (
                        <Box
                            key={banner.key ?? banner.src}
                            onClick={() => (isActive ? banner.onClick() : setActive(index))}
                            role="button"
                            tabIndex={isActive ? 0 : -1}
                            aria-label={banner.label}
                            aria-hidden={!isActive}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") banner.onClick();
                                if (e.key === "ArrowRight") go(1);
                                if (e.key === "ArrowLeft") go(-1);
                            }}
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                width: CARD_WIDTH,
                                aspectRatio: ratio,
                                borderRadius: { xs: 3, md: 4 },
                                overflow: "hidden",
                                cursor: "pointer",
                                bgcolor: "grey.900",
                                zIndex: isActive ? 3 : 1,
                                visibility: isNear ? "visible" : "hidden",
                                boxShadow: isActive
                                    ? "0 24px 48px -12px rgba(26,26,46,0.35)"
                                    : "0 8px 20px rgba(26,26,46,0.10)",
                                transform: `translate(-50%, -50%) translateX(${offset * SIDE_SHIFT}%) scale(${
                                    isActive ? 1 : SIDE_SCALE
                                })`,
                                transition: reduceMotion
                                    ? "none"
                                    : "transform .7s cubic-bezier(.22,.61,.36,1), box-shadow .7s ease",
                                "&:hover .promo-image": isActive ? { transform: "scale(1.03)" } : {},
                                "&:hover .promo-cta": isActive ? { gap: 1.25 } : {},
                            }}
                        >
                            <Box
                                className="promo-image"
                                component="img"
                                src={banner.src}
                                alt={banner.alt}
                                loading={index === 0 ? "eager" : "lazy"}
                                draggable={false}
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: { xs: "30% center", md: "left center" },
                                    userSelect: "none",
                                    transition: reduceMotion ? "none" : "transform 1.2s ease",
                                }}
                            />
                            {banner.content && <BannerCaption content={banner.content} visible={isActive} />}
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    bgcolor: "rgba(10,14,30,0.45)",
                                    opacity: isActive ? 0 : 1,
                                    transition: reduceMotion ? "none" : "opacity .7s ease",
                                    pointerEvents: "none",
                                }}
                            />
                        </Box>
                    );
                })}
            </Box>

            {isDesktop && count > 1 && (
                <>
                    <SliderArrow side="left" onClick={() => go(-1)} />
                    <SliderArrow side="right" onClick={() => go(1)} />
                </>
            )}

            {count > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: { xs: 2, md: 3 } }}>
                    {banners.map((banner, index) => {
                        const isActive = index === active;
                        return (
                            <Box
                                key={`dot-${banner.key ?? banner.src}`}
                                onClick={() => setActive(index)}
                                role="button"
                                aria-label={`${index + 1}. baner`}
                                aria-current={isActive ? "true" : undefined}
                                sx={{
                                    position: "relative",
                                    width: isActive ? 36 : 8,
                                    height: 8,
                                    borderRadius: 4,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    bgcolor: "grey.300",
                                    transition: "width .3s ease",
                                }}
                            >
                                {isActive && (
                                    <Box
                                        key={`${active}-${autoplay}`}
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            bgcolor: "primary.main",
                                            transformOrigin: "left center",
                                            animation: autoplay ? `${fill} ${AUTOPLAY_MS}ms linear forwards` : "none",
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}

function SliderArrow({ side, onClick }) {
    return (
        <IconButton
            onClick={onClick}
            aria-label={side === "left" ? "Prethodni baner" : "Sledeći baner"}
            sx={{
                position: "absolute",
                top: "calc(50% - 20px)",
                [side]: { md: 4, lg: -4 },
                transform: "translateY(-50%)",
                zIndex: 4,
                width: 48,
                height: 48,
                bgcolor: "white",
                color: "grey.800",
                boxShadow: "0 6px 20px rgba(26,26,46,0.18)",
                transition: "transform .2s ease, box-shadow .2s ease",
                "&:hover": {
                    bgcolor: "white",
                    transform: "translateY(-50%) scale(1.08)",
                    boxShadow: "0 8px 24px rgba(26,26,46,0.26)",
                },
            }}
        >
            <Icon icon={side === "left" ? "mdi:chevron-left" : "mdi:chevron-right"} width={26} />
        </IconButton>
    );
}

function BannerCaption({ content, visible }) {
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: { xs: "flex-end", md: "center" },
                color: "common.white",
                pointerEvents: "none",
                background: {
                    xs: "linear-gradient(0deg, rgba(10,14,30,0.92) 0%, rgba(10,14,30,0.55) 55%, rgba(10,14,30,0.05) 100%)",
                    md: "linear-gradient(90deg, rgba(10,14,30,0.88) 0%, rgba(10,14,30,0.6) 42%, rgba(10,14,30,0) 72%)",
                },
            }}
        >
            <Box
                sx={{
                    p: { xs: 2, sm: 3, md: 5 },
                    width: "100%",
                    maxWidth: { md: "56%" },
                    opacity: visible ? 1 : 0,
                    transform: visible ? "none" : "translateY(12px)",
                    transition: "opacity .5s ease .2s, transform .5s ease .2s",
                }}
            >
                {content.carrier && (
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.25,
                            py: 0.4,
                            mb: { xs: 1, md: 1.5 },
                            borderRadius: 5,
                            bgcolor: "rgba(255,255,255,0.16)",
                            border: "1px solid rgba(255,255,255,0.28)",
                            backdropFilter: "blur(6px)",
                            fontSize: { xs: "0.72rem", md: "0.8rem" },
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                        }}
                    >
                        <Icon icon="mdi:bus" width={15} />
                        {content.carrier}
                    </Box>
                )}

                <Typography
                    component="h2"
                    sx={{
                        fontWeight: 800,
                        lineHeight: 1.12,
                        letterSpacing: "-0.01em",
                        fontSize: { xs: "1.3rem", sm: "1.7rem", md: "2.4rem" },
                        textShadow: "0 2px 12px rgba(0,0,0,0.25)",
                    }}
                >
                    {content.title}
                </Typography>

                {content.from && content.to && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{ mt: { xs: 0.75, md: 1.25 }, opacity: 0.92, fontWeight: 600, fontSize: { xs: "0.85rem", md: "1rem" } }}
                    >
                        <Icon icon="mdi:map-marker-outline" width={18} />
                        <span>{content.from}</span>
                        <Icon icon="mdi:arrow-right" width={16} />
                        <span>{content.to}</span>
                    </Stack>
                )}

                {content.subtitle && (
                    <Typography
                        component="h3"
                        sx={{
                            display: { xs: "none", sm: "block" },
                            mt: { sm: 1, md: 1.5 },
                            fontWeight: 600,
                            lineHeight: 1.35,
                            fontSize: { sm: "1rem", md: "1.2rem" },
                        }}
                    >
                        {content.subtitle}
                    </Typography>
                )}

                {content.body && (
                    <Typography
                        sx={{ display: { xs: "none", md: "block" }, mt: 1, opacity: 0.85, fontSize: "0.95rem", lineHeight: 1.6 }}
                    >
                        {content.body}
                    </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }} sx={{ mt: { xs: 1.5, md: 3 } }}>
                    {content.price && (
                        <Box
                            sx={{
                                px: { xs: 1.25, md: 2 },
                                py: { xs: 0.5, md: 0.75 },
                                borderRadius: 2,
                                bgcolor: "common.white",
                                color: "grey.900",
                                lineHeight: 1.1,
                                boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                            }}
                        >
                            {content.priceFrom && (
                                <Box
                                    sx={{
                                        fontSize: { xs: "0.65rem", md: "0.72rem" },
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                        color: "grey.600",
                                    }}
                                >
                                    {content.priceFrom}
                                </Box>
                            )}
                            <Box sx={{ fontWeight: 800, fontSize: { xs: "1.05rem", md: "1.5rem" }, whiteSpace: "nowrap" }}>
                                {content.price}
                            </Box>
                        </Box>
                    )}
                    {content.cta && (
                        <Box
                            className="promo-cta"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                fontWeight: 700,
                                fontSize: { xs: "0.85rem", md: "1rem" },
                                transition: "gap .25s ease",
                                "& .cta-text": { display: { xs: "none", sm: "inline" } },
                            }}
                        >
                            <span className="cta-text">{content.cta}</span>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: { xs: 32, md: 36 },
                                    height: { xs: 32, md: 36 },
                                    borderRadius: "50%",
                                    border: "1.5px solid rgba(255,255,255,0.85)",
                                }}
                            >
                                <Icon icon="mdi:arrow-right" width={18} />
                            </Box>
                        </Box>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}
